import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft,
  PencilSimple,
  Copy,
  Trash,
  Play,
  Calendar,
  User,
  Tag,
  Flask,
  Scroll
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import FileAttachments from '../../components/FileAttachments';

const statusColors = {
  planned: 'status-planned',
  in_progress: 'status-in-progress',
  completed: 'status-completed',
  failed: 'status-failed'
};

const ExperimentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [experiment, setExperiment] = useState(null);
  const [protocol, setProtocol] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadExperiment();
  }, [id]);

  const loadExperiment = async () => {
    try {
      const data = await api.getExperiment(id);
      setExperiment(data);
      if (data.protocol_id) {
        const protoData = await api.getProtocol(data.protocol_id);
        setProtocol(protoData);
      }
    } catch (error) {
      console.error('Failed to load experiment:', error);
      toast.error('Failed to load experiment');
    } finally {
      setLoading(false);
    }
  };

  const handleClone = async () => {
    try {
      const cloned = await api.cloneExperiment(id);
      toast.success('Experiment cloned');
      navigate(`/experiments/${cloned.id}`);
    } catch (error) {
      toast.error('Failed to clone experiment');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this experiment?')) {
      try {
        await api.deleteExperiment(id);
        toast.success('Experiment deleted');
        navigate('/experiments');
      } catch (error) {
        toast.error('Failed to delete experiment');
      }
    }
  };

  const handleStartBenchMode = () => {
    if (protocol) {
      navigate(`/protocols/${protocol.id}/bench`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  if (!experiment) {
    return (
      <div className="text-center py-12">
        <p className="text-zinc-500">Experiment not found</p>
        <Link to="/experiments" className="text-indigo-400 hover:underline mt-2 inline-block">
          Back to experiments
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="experiment-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/experiments" className="p-2 hover:bg-zinc-100 rounded-sm">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="heading-display">{experiment.title}</h1>
              <span className={statusColors[experiment.status]}>{experiment.status?.replace('_', ' ')}</span>
            </div>
            <p className="text-zinc-500 mt-1">Created {new Date(experiment.created_at).toLocaleDateString()}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {protocol && (
            <Button variant="outline" className="btn-secondary flex items-center gap-2" onClick={handleStartBenchMode} data-testid="bench-mode-btn">
              <Play size={16} /> Bench Mode
            </Button>
          )}
          <Link to={`/experiments/${id}/edit`}>
            <Button variant="outline" className="btn-secondary flex items-center gap-2" data-testid="edit-experiment-btn">
              <PencilSimple size={16} /> Edit
            </Button>
          </Link>
          <Button variant="outline" className="btn-secondary flex items-center gap-2" onClick={handleClone}>
            <Copy size={16} /> Clone
          </Button>
          <Button variant="destructive" className="bg-red-600 text-white hover:bg-red-700 flex items-center gap-2" onClick={handleDelete}>
            <Trash size={16} /> Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          {experiment.description && (
            <div className="card-flat p-6">
              <h2 className="heading-card mb-3">Description</h2>
              <p className="text-zinc-700 whitespace-pre-wrap">{experiment.description}</p>
            </div>
          )}

          {/* Hypothesis */}
          {experiment.hypothesis && (
            <div className="card-flat p-6">
              <h2 className="heading-card mb-3">Hypothesis</h2>
              <p className="text-zinc-700 whitespace-pre-wrap">{experiment.hypothesis}</p>
            </div>
          )}

          {/* Materials */}
          {experiment.materials?.length > 0 && (
            <div className="card-flat p-6">
              <h2 className="heading-card mb-3 flex items-center gap-2">
                <Flask size={20} /> Materials
              </h2>
              <ul className="list-disc list-inside space-y-1">
                {experiment.materials.map((material, idx) => (
                  <li key={idx} className="text-zinc-700">{material}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Protocol */}
          {protocol && (
            <div className="card-flat p-6">
              <h2 className="heading-card mb-3 flex items-center gap-2">
                <Scroll size={20} /> Protocol
              </h2>
              <Link to={`/protocols/${protocol.id}`} className="text-indigo-400 hover:underline font-medium">
                {protocol.title}
              </Link>
              {protocol.steps?.length > 0 && (
                <div className="mt-4 space-y-2">
                  {protocol.steps.slice(0, 3).map((step, idx) => (
                    <div key={idx} className="protocol-step">
                      <div className="protocol-step-number">{idx + 1}</div>
                      <div className="protocol-step-content">
                        <p className="text-sm text-zinc-700">{step.instruction || step.title || step}</p>
                      </div>
                    </div>
                  ))}
                  {protocol.steps.length > 3 && (
                    <p className="text-sm text-zinc-500 pl-10">... and {protocol.steps.length - 3} more steps</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Results */}
          {experiment.results && (
            <div className="card-flat p-6">
              <h2 className="heading-card mb-3">Results</h2>
              <p className="text-zinc-700 whitespace-pre-wrap">{experiment.results}</p>
            </div>
          )}

          {/* Conclusion */}
          {experiment.conclusion && (
            <div className="card-flat p-6">
              <h2 className="heading-card mb-3">Conclusion</h2>
              <p className="text-zinc-700 whitespace-pre-wrap">{experiment.conclusion}</p>
            </div>
          )}

          {/* Failure Reason */}
          {experiment.failure_reason && (
            <div className="card-flat p-6 border-red-800/40 bg-red-900/20">
              <h2 className="heading-card mb-3 text-red-400">Failure Reason</h2>
              <p className="text-red-400 whitespace-pre-wrap">{experiment.failure_reason}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Metadata */}
          <div className="card-flat p-6 space-y-4">
            <h3 className="heading-card">Details</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <Calendar size={16} className="text-zinc-400" />
                <span className="text-zinc-500">Created:</span>
                <span className="text-slate-100">{new Date(experiment.created_at).toLocaleString()}</span>
              </div>
              {experiment.updated_at && (
                <div className="flex items-center gap-3 text-sm">
                  <Calendar size={16} className="text-zinc-400" />
                  <span className="text-zinc-500">Updated:</span>
                  <span className="text-slate-100">{new Date(experiment.updated_at).toLocaleString()}</span>
                </div>
              )}
              {experiment.created_by && (
                <div className="flex items-center gap-3 text-sm">
                  <User size={16} className="text-zinc-400" />
                  <span className="text-zinc-500">Created by:</span>
                  <span className="text-slate-100">{experiment.created_by}</span>
                </div>
              )}
            </div>
          </div>

          {/* Tags */}
          {experiment.tags?.length > 0 && (
            <div className="card-flat p-6">
              <h3 className="heading-card mb-3 flex items-center gap-2">
                <Tag size={16} /> Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {experiment.tags.map((tag, idx) => (
                  <span key={idx} className="bg-zinc-100 text-zinc-700 px-2 py-1 text-sm rounded-sm">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* File Attachments */}
          <div className="card-flat p-5">
            <FileAttachments entityType="experiments" entityId={id} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExperimentDetail;
